// ============================================
// EMPRESADATOS.JS — Administracion > Datos de la empresa
// ============================================
// Editable por direccion/administracion SIN codigo: datos fiscales del emisor (salen en el
// PDF), cuentas bancarias (N, con "Principal" y "Mostrar en cotizaciones"), garantia de
// servicios Beep (instalacion en meses, servicio en dias) y los textos del certificado de
// garantia (que cubre, apertura de casos no cubiertos, 8 tarjetas activables, visita de
// diagnostico, contacto, terminos comerciales). Placeholders claros: NO se inventan datos.
// Backend: GET/PUT /api/empresa, /api/empresa/cuentas, /api/empresa/casos (soloAdmin en escritura).
// Responsive: una columna en angosto (useAnchoViewport < 1100), dos en ancho.

import React, { useCallback, useEffect, useState } from 'react';
import DocumentosEmpresa from './components/DocumentosEmpresa';
import ClausulasCotizacionAdmin from './components/ClausulasCotizacionAdmin';
import { Building2, Landmark, ShieldCheck, Plus, Trash2, Star, Save } from 'lucide-react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import { useAnchoViewport } from './hooks/useResponsive';

function authHeaders() {
  const t = localStorage.getItem('beep_token');
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

const lbl = { fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 6, fontWeight: 600 };
const inp = { ...estiloInput, fontSize: 16, minHeight: 44 };
const area = { ...estiloInput, fontSize: 15, minHeight: 90, resize: 'vertical', fontFamily: PP, lineHeight: 1.45 };

const CAMPOS_FISCALES = [
  ['razon_social', 'Razón social', 'Ej. Comercializadora Beep México S.A. de C.V.'],
  ['rfc', 'RFC', 'Pendiente de capturar'],
  ['direccion_fiscal', 'Dirección fiscal', 'Calle, número, colonia, C.P., ciudad'],
  ['telefono', 'Teléfono', 'Ej. 55 0000 0000'],
  ['correo', 'Correo', 'Ej. contacto@beepmx.com'],
  ['correo_comprobantes', 'Correo para comprobantes de pago', 'Ej. pagos@beepmx.com (sale en la tarjeta de transferencia)'],
  ['correo_pagos', 'Correo para aclaraciones de pagos', 'Ej. avaldez@beepmx.com (sale en la carta de datos bancarios)'],
  ['representante_nombre', 'Representante legal (nombre) — no se imprime en la carta bancaria', 'Ej. Ricardo Herrera Colorado'],
  ['representante_cargo', 'Representante legal (cargo) — no se imprime en la carta bancaria', 'Ej. Director General'],
  ['web', 'Sitio web', 'Ej. beepmx.com'],
];

const CUENTA_VACIA = { banco: '', moneda: 'MXN', beneficiario: '', numero_cuenta: '', clabe: '', swift: '', sucursal: '', mostrar_en_cotizaciones: true, principal: false };

export default function EmpresaDatos() {
  const ancho = useAnchoViewport();
  const compacto = ancho < 1100;
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [casos, setCasos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);
  const [cuentaEdit, setCuentaEdit] = useState(null); // null | { ...cuenta } (id null = nueva)
  const [error, setError] = useState(null);

  const avisar = (txt, tipo = 'ok') => { setToast({ txt, tipo }); setTimeout(() => setToast(null), 4500); };

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/empresa`, { headers: authHeaders() });
      if (r.status === 403 || r.status === 401) { setError('No tienes acceso a los datos de la empresa.'); return; }
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || 'No se pudieron leer los datos');
      setData(j); setForm(j.empresa || {}); setCasos(j.casos || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardarEmpresa = async () => {
    if (!form) return;
    setGuardando(true);
    try {
      const body = {};
      for (const k of ['razon_social', 'rfc', 'direccion_fiscal', 'telefono', 'correo', 'correo_comprobantes', 'correo_pagos', 'carta_bancaria_vigencia_dias', 'representante_nombre', 'representante_cargo', 'web', 'garantia_instalacion_meses', 'garantia_servicio_dias',
        'garantia_que_cubre', 'garantia_apertura_no_cubiertos', 'garantia_visita_diagnostico', 'garantia_contacto', 'terminos_comerciales']) body[k] = form[k] ?? null;
      const r = await fetch(`${API_BASE_CLI}/api/empresa`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 403) return avisar('Solo Dirección/Administración puede editar estos datos.', 'error');
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo guardar');
      const rc = await fetch(`${API_BASE_CLI}/api/empresa/casos`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ casos }) });
      const jc = await rc.json().catch(() => ({}));
      if (!rc.ok || !jc.ok) throw new Error(jc.error || 'No se pudieron guardar los casos no cubiertos');
      setData(jc); setForm(jc.empresa); setCasos(jc.casos);
      avisar('Datos de la empresa guardados. Ya salen en los PDF nuevos.');
    } catch (e) { avisar(e.message, 'error'); }
    finally { setGuardando(false); }
  };

  const guardarCuenta = async () => {
    if (!cuentaEdit) return;
    if (!cuentaEdit.banco.trim()) return avisar('El banco es obligatorio.', 'error');
    setGuardando(true);
    try {
      const esNueva = !cuentaEdit.id;
      const r = await fetch(`${API_BASE_CLI}/api/empresa/cuentas${esNueva ? '' : '/' + cuentaEdit.id}`, { method: esNueva ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(cuentaEdit) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo guardar la cuenta');
      setData(j); setCuentaEdit(null);
      avisar(esNueva ? 'Cuenta agregada.' : 'Cuenta actualizada.');
    } catch (e) { avisar(e.message, 'error'); }
    finally { setGuardando(false); }
  };

  const eliminarCuenta = async (c) => {
    if (!window.confirm(`¿Eliminar la cuenta de ${c.banco}? Dejará de aparecer en las cotizaciones nuevas.`)) return;
    try {
      const r = await fetch(`${API_BASE_CLI}/api/empresa/cuentas/${c.id}`, { method: 'DELETE', headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo eliminar');
      setData(j); avisar('Cuenta eliminada.');
    } catch (e) { avisar(e.message, 'error'); }
  };

  const hacerPrincipal = async (c) => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/empresa/cuentas/${c.id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ ...c, principal: true }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo marcar como principal');
      setData(j); avisar(`${c.banco} es ahora la cuenta principal.`);
    } catch (e) { avisar(e.message, 'error'); }
  };

  if (error) return <div style={{ fontFamily: PP, color: '#dc2626', padding: 20 }}>{error}</div>;
  if (!data || !form) return <div style={{ fontFamily: PP, color: COLOR_GRIS_CLARO, padding: 20 }}>Cargando datos de la empresa…</div>;

  const faltanFiscales = CAMPOS_FISCALES.filter(([k]) => !['web', 'correo_comprobantes', 'correo_pagos', 'representante_nombre', 'representante_cargo'].includes(k) && !(form[k] || '').trim()).length;
  const grid2 = { display: 'grid', gridTemplateColumns: compacto ? '1fr' : '1fr 1fr', gap: 14 };

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, display: 'grid', gap: 16, maxWidth: 1100 }}>
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>Datos de la empresa</div>
        <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO, marginTop: 4 }}>Lo que aparece en las cotizaciones y en el certificado de garantía. Se edita aquí, sin tocar código.</div>
      </div>

      {faltanFiscales > 0 && (
        <div style={{ padding: 12, background: '#FFF6E5', border: '1px solid #F3D9A4', borderRadius: 10, fontSize: 13, color: '#9A5B00' }}>
          Faltan {faltanFiscales} datos fiscales por capturar (RFC, dirección, teléfono o correo). Hasta entonces el PDF los omite; no se inventan.
        </div>
      )}

      {/* Datos fiscales */}
      <div style={{ ...estiloTarjeta, padding: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><Building2 size={18} color="#265F97" /> Datos fiscales del emisor</div>
        <div style={grid2}>
          {CAMPOS_FISCALES.map(([k, l, ph]) => (
            <div key={k} style={k === 'direccion_fiscal' && !compacto ? { gridColumn: '1 / -1' } : undefined}>
              <div style={lbl}>{l}</div>
              <input style={inp} value={form[k] || ''} placeholder={ph} onChange={e => set(k, e.target.value)} />
            </div>
          ))}
        </div>
        {/* CARTA-BANCARIA v2: vigencia por defecto de la carta libre (la que nace desde una cotizacion
            hereda la vigencia de esa cotizacion y no usa este valor). */}
        {/* La ayuda ocupa la segunda columna en escritorio para no dejar medio renglon vacio; en
            compacto (incluido PC vertical <1100) el grid cae a 1 columna y queda debajo del campo. */}
        <div style={{ ...grid2, marginTop: 14, alignItems: 'start' }}>
          <div>
            <div style={lbl}>Vigencia por defecto de la carta de datos bancarios (días)</div>
            <input style={inp} type="number" min="1" max="365" value={form.carta_bancaria_vigencia_dias ?? 15} onChange={e => set('carta_bancaria_vigencia_dias', e.target.value)} />
          </div>
          <div style={{ fontSize: 11.5, color: COLOR_GRIS_CLARO, lineHeight: 1.5, marginTop: compacto ? 0 : 24 }}>Se usa cuando generas la carta desde aquí. Desde una cotización, la carta toma la vigencia de esa cotización.</div>
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 10 }}>Logo: se usa el logo institucional actual de la aplicación.</div>
      </div>

      {/* Cuentas bancarias */}
      <div style={{ ...estiloTarjeta, padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 8 }}><Landmark size={18} color="#265F97" /> Cuentas bancarias</div>
          <button type="button" style={{ ...estiloBotonSecundario, marginLeft: 'auto', minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setCuentaEdit({ ...CUENTA_VACIA })}><Plus size={16} /> Agregar cuenta</button>
        </div>
        {data.cuentas.length === 0 && <div style={{ fontSize: 13, color: COLOR_GRIS_CLARO }}>Aún no hay cuentas. Agrega la primera: saldrá como principal en las cotizaciones.</div>}
        <div style={{ display: 'grid', gap: 10 }}>
          {data.cuentas.map(c => (
            <div key={c.id} style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, padding: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', background: c.principal ? '#EEF6FF' : '#fff' }}>
              <div style={{ flex: '1 1 240px' }}>
                <div style={{ fontWeight: 700, color: COLOR_OSCURO, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.banco} <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: '#265F97', fontWeight: 700 }}>{c.moneda}</span>
                  {c.principal && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#265F97', color: '#fff', fontWeight: 700 }}>Principal</span>}
                  {!c.mostrar_en_cotizaciones && <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>(oculta en cotizaciones)</span>}
                </div>
                <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 4, lineHeight: 1.6 }}>
                  {c.beneficiario && <div>Beneficiario: {c.beneficiario}</div>}
                  {c.numero_cuenta && <div>Cuenta: {c.numero_cuenta}</div>}
                  {c.clabe && <div>CLABE: <span style={{ fontFamily: 'monospace' }}>{c.clabe}</span></div>}
                  {c.swift && <div>SWIFT: {c.swift}</div>}
                  {c.sucursal && <div>Sucursal: {c.sucursal}</div>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!c.principal && <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44, display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => hacerPrincipal(c)}><Star size={15} /> Principal</button>}
                <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44 }} onClick={() => setCuentaEdit({ ...c })}>Editar</button>
                <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44, color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => eliminarCuenta(c)}><Trash2 size={15} /> Eliminar</button>
              </div>
            </div>
          ))}
        </div>

        {cuentaEdit && (
          <div style={{ marginTop: 14, padding: 14, border: `1px dashed #265F97`, borderRadius: 12, background: '#F8FBFF' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>{cuentaEdit.id ? 'Editar cuenta' : 'Nueva cuenta'}</div>
            <div style={grid2}>
              <div><div style={lbl}>Banco *</div><input style={inp} value={cuentaEdit.banco} onChange={e => setCuentaEdit(c => ({ ...c, banco: e.target.value }))} placeholder="Ej. BBVA" /></div>
              <div><div style={lbl}>Moneda</div>
                <select style={inp} value={cuentaEdit.moneda} onChange={e => setCuentaEdit(c => ({ ...c, moneda: e.target.value }))}><option value="MXN">MXN</option><option value="USD">USD</option></select></div>
              <div><div style={lbl}>Beneficiario</div><input style={inp} value={cuentaEdit.beneficiario || ''} onChange={e => setCuentaEdit(c => ({ ...c, beneficiario: e.target.value }))} placeholder="Razón social del beneficiario" /></div>
              <div><div style={lbl}>Número de cuenta</div><input style={inp} value={cuentaEdit.numero_cuenta || ''} onChange={e => setCuentaEdit(c => ({ ...c, numero_cuenta: e.target.value }))} /></div>
              <div><div style={lbl}>CLABE (18 dígitos)</div><input style={{ ...inp, fontFamily: 'monospace' }} inputMode="numeric" value={cuentaEdit.clabe || ''} onChange={e => setCuentaEdit(c => ({ ...c, clabe: e.target.value.replace(/\D/g, '').slice(0, 18) }))} /></div>
              <div><div style={lbl}>SWIFT (opcional)</div><input style={inp} value={cuentaEdit.swift || ''} onChange={e => setCuentaEdit(c => ({ ...c, swift: e.target.value.toUpperCase() }))} /></div>
              <div><div style={lbl}>Sucursal (opcional)</div><input style={inp} value={cuentaEdit.sucursal || ''} onChange={e => setCuentaEdit(c => ({ ...c, sucursal: e.target.value }))} placeholder="Ej. 3476 CDMX Tlalpan Huipulco" /></div>
            </div>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 12, fontSize: 13 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}><input type="checkbox" style={{ width: 20, height: 20 }} checked={!!cuentaEdit.mostrar_en_cotizaciones} onChange={e => setCuentaEdit(c => ({ ...c, mostrar_en_cotizaciones: e.target.checked }))} /> Mostrar en cotizaciones</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, cursor: 'pointer' }}><input type="checkbox" style={{ width: 20, height: 20 }} checked={!!cuentaEdit.principal} onChange={e => setCuentaEdit(c => ({ ...c, principal: e.target.checked }))} /> Principal (sale por default)</label>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
              <button type="button" style={{ ...estiloBotonPrimario, minHeight: 44 }} disabled={guardando} onClick={guardarCuenta}>{guardando ? 'Guardando…' : 'Guardar cuenta'}</button>
              <button type="button" style={{ ...estiloBotonSecundario, minHeight: 44 }} onClick={() => setCuentaEdit(null)}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* DOCUMENTOS DE LA EMPRESA (kit de proveedor, 2026-09-08) */}
      <DocumentosEmpresa compacto={compacto} />

      {/* Garantia */}
      <div style={{ ...estiloTarjeta, padding: 18 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><ShieldCheck size={18} color="#265F97" /> Garantía de servicios Beep y textos del certificado</div>
        <div style={grid2}>
          <div><div style={lbl}>Instalación: garantía de mano de obra (meses)</div><input style={inp} type="number" min="1" value={form.garantia_instalacion_meses ?? 12} onChange={e => set('garantia_instalacion_meses', e.target.value)} /></div>
          <div><div style={lbl}>Servicio / reparación: garantía (días)</div><input style={inp} type="number" min="1" value={form.garantia_servicio_dias ?? 90} onChange={e => set('garantia_servicio_dias', e.target.value)} /></div>
        </div>
        <div style={{ marginTop: 12 }}><div style={lbl}>Qué cubre</div><textarea style={area} value={form.garantia_que_cubre || ''} onChange={e => set('garantia_que_cubre', e.target.value)} /></div>
        <div style={{ marginTop: 12 }}><div style={lbl}>Apertura de "Casos no cubiertos"</div><textarea style={{ ...area, minHeight: 70 }} value={form.garantia_apertura_no_cubiertos || ''} onChange={e => set('garantia_apertura_no_cubiertos', e.target.value)} /></div>

        <div style={{ marginTop: 14 }}>
          <div style={lbl}>Casos no cubiertos (tarjetas del certificado): activa y edita cada una</div>
          <div style={grid2}>
            {casos.map((c, i) => (
              <div key={c.id} style={{ border: `1px solid ${COLOR_BORDE}`, borderRadius: 12, padding: 12, opacity: c.activo ? 1 : 0.55 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: COLOR_OSCURO }}>
                  <input type="checkbox" style={{ width: 20, height: 20 }} checked={!!c.activo} onChange={e => setCasos(cs => cs.map((x, j) => j === i ? { ...x, activo: e.target.checked } : x))} />
                  <input style={{ ...inp, minHeight: 40, fontWeight: 700 }} value={c.titulo || ''} onChange={e => setCasos(cs => cs.map((x, j) => j === i ? { ...x, titulo: e.target.value } : x))} />
                </label>
                <textarea style={{ ...area, minHeight: 56, fontSize: 13, marginTop: 6 }} value={c.descripcion || ''} onChange={e => setCasos(cs => cs.map((x, j) => j === i ? { ...x, descripcion: e.target.value } : x))} />
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12 }}><div style={lbl}>Visita de diagnóstico</div><textarea style={{ ...area, minHeight: 60 }} value={form.garantia_visita_diagnostico || ''} onChange={e => set('garantia_visita_diagnostico', e.target.value)} /></div>
        <div style={{ marginTop: 12 }}><div style={lbl}>Contacto para hacer válida la garantía</div><textarea style={{ ...area, minHeight: 56 }} value={form.garantia_contacto || ''} onChange={e => set('garantia_contacto', e.target.value)} placeholder="Ej. Escríbanos a soporte@… o llame al 55 …, indicando el folio del certificado." /></div>
        <div style={{ marginTop: 12 }}><div style={lbl}>Términos comerciales generales (página de pago; solo en cotizaciones anteriores a «Términos y condiciones»)</div><textarea style={area} value={form.terminos_comerciales || ''} onChange={e => set('terminos_comerciales', e.target.value)} /></div>
      </div>

      {/* TERMINOS-COTIZACION (2026-10-05): catalogo de clausulas. Se guarda clausula a clausula
          con sus propios botones (no con "Guardar datos de la empresa"). */}
      <ClausulasCotizacionAdmin compacto={compacto} />

      <div style={{ position: 'sticky', bottom: 0, background: '#fff', padding: '10px 0 4px', borderTop: `1px solid ${COLOR_BORDE}`, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', zIndex: 5 }}>
        <button type="button" style={{ ...estiloBotonPrimario, minHeight: 46, display: 'inline-flex', alignItems: 'center', gap: 8 }} disabled={guardando} onClick={guardarEmpresa}><Save size={16} /> {guardando ? 'Guardando…' : 'Guardar datos de la empresa'}</button>
        {data.empresa?.actualizado_at && <span style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Última edición: {new Date(data.empresa.actualizado_at).toLocaleString('es-MX')}{data.empresa.actualizado_por ? ` · ${data.empresa.actualizado_por}` : ''}</span>}
        {toast && <span style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 600, color: toast.tipo === 'error' ? '#dc2626' : '#16a34a' }}>{toast.txt}</span>}
      </div>
    </div>
  );
}
